import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany, useMoneyFmt } from "@/lib/company";
import { useAuth } from "@/lib/auth";
import { useClientUnread } from "@/lib/useClientUnread";
import { toast } from "sonner";
import { BooksCard, ProfitCard, CashCard, AttentionCard, TeamStrip } from "@/components/owner/OverviewCards";
import MoneyTab from "@/components/owner/MoneyTab";
import DocumentsTab from "@/components/owner/DocumentsTab";
import TeamTab from "@/components/owner/TeamTab";
import MessagesTab from "@/components/owner/MessagesTab";
import { MissingReceiptsModal } from "@/components/owner/MissingReceiptsModal";
import { OverdueInvoicesModal } from "@/components/owner/OverdueInvoicesModal";
import { StatementUploadModal } from "@/components/owner/StatementUploadModal";
import { Button } from "@/components/owner/ui";
import { Gate } from "@/components/Entitlements";

const TABS = [
  { key: "overview", label: "Overview", path: "/owner" },
  { key: "money", label: "Money", path: "/owner/money" },
  { key: "documents", label: "Documents", path: "/owner/documents" },
  { key: "team", label: "Your team", path: "/owner/team" },
  { key: "messages", label: "Messages", path: "/owner/messages", needsMessages: true },
];

const HEADLINES = {
  overview: (d) => [`${d.user.greeting}${d.user.first_name ? `, ${d.user.first_name}` : ""}.`, `Here's where ${d.company.name} stands — numbers pulled live from your books and bank.`],
  money: () => ["Know what's coming and going.", "Cash, collections and commitments in one place."],
  documents: () => ["A home for your paperwork.", "Send it once. Your bookkeeping team handles the rest."],
  team: () => ["Your books have a team behind them.", "See what's done, what's next, and where you can help."],
  messages: () => ["Messages with your accountant.", "Questions you've asked, what they answered, and what's still waiting on you."],
};

function periodOptions() {
  const out = [];
  const d = new Date(); d.setDate(1);
  for (let i = 0; i < 8; i++) {
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({ ym, label: d.toLocaleDateString("en-US", { month: "long", year: "numeric" }) });
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

export default function OwnerDashboard() {
  const { tab = "overview" } = useParams();
  const navigate = useNavigate();
  const { currentId } = useCompany();
  const { user } = useAuth();
  const fmt = useMoneyFmt();
  const [period, setPeriod] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const options = useMemo(periodOptions, []);
  const [msgCount, setMsgCount] = useState(0);
  useEffect(() => {
    if (!currentId) return;
    api.get(`/companies/${currentId}/client-messages`).then(r => setMsgCount((r.data?.messages || []).length)).catch(() => setMsgCount(0));
  }, [currentId]);
  const unread = useClientUnread(currentId);
  const load = useCallback(async () => {
    if (!currentId) return;
    setLoading(true);
    try {
      const r = await api.get(`/companies/${currentId}/owner-dashboard`, { params: period ? { period } : {} });
      setData(r.data);
      if (!period) setPeriod(r.data.period.ym);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load your dashboard.");
    } finally { setLoading(false); }
  }, [currentId, period]);

  useEffect(() => { load(); }, [load]);

  const [receiptsOpen, setReceiptsOpen] = useState(false);
  const [overdueOpen, setOverdueOpen] = useState(false);
  const [stmtItem, setStmtItem] = useState(null);

  const onAttention = (it) => {
    if (it.id === "receipts" && (it.items || []).some((m) => m.transaction)) { setReceiptsOpen(true); return; }
    if (it.id === "overdue") { setOverdueOpen(true); return; }
    if (it.kind === "statement" && it.account_id) { setStmtItem(it); return; }
    if (it.href) navigate(it.href);
  };
  const startCatchup = () => navigate("/accounting/todo?view=cleanup");

  if (!currentId) {
    return <div className="p-10 text-slate-500 text-sm" data-testid="owner-dashboard-no-company">No company is linked to your account yet. Your bookkeeper will connect one shortly.</div>;
  }
  if (!data) {
    return <div className="flex items-center gap-2 text-slate-500 p-10" data-testid="owner-dashboard-loading"><Loader2 className="animate-spin" size={16} /> Loading your business…</div>;
  }
  const [h1, sub] = HEADLINES[tab](data);
  const b = data.banner;

  return (
    <div className="max-w-[1180px] mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="owner-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <nav className="flex gap-1 bg-slate-100 p-1 rounded-xl" data-testid="owner-tabs">
          {TABS.filter(t => !t.needsMessages || msgCount > 0 || tab === "messages").map(t => {
            const hot = t.key === "messages" && unread > 0;
            return (
              <button key={t.key} onClick={() => navigate(t.path)} data-unread={hot ? "true" : "false"}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${tab === t.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"} ${hot ? "ring-1 ring-inset ring-red-500" : ""}`}
                data-testid={`owner-tab-${t.key}`} title={hot ? `${unread} outstanding message${unread === 1 ? "" : "s"} — your turn` : undefined}>
                {t.label}
                {hot && <span className="inline-flex items-center gap-0.5 text-red-600" data-testid="owner-tab-messages-bell"><Bell size={13} className="fill-red-600" /><span className="text-[11px] font-bold">{unread}</span></span>}
              </button>
            );
          })}
        </nav>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {loading && <Loader2 className="animate-spin" size={14} />}
          <span>{user?.role === "client" ? "" : "Previewing as owner · "}</span>
          <label className="flex items-center gap-2 border border-slate-200 bg-white rounded-lg px-3 py-1.5">
            <span className="text-[10px] uppercase tracking-[0.08em]">Period</span>
            <select value={period} onChange={e => setPeriod(e.target.value)} className="text-sm font-semibold text-slate-900 bg-transparent outline-none" data-testid="owner-period-select">
              {options.map(o => <option key={o.ym} value={o.ym}>{o.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-slate-900" data-testid="owner-headline">{h1}</h1>
          <p className="text-slate-500 mt-1 text-sm">{sub}</p>
        </div>
      </div>

      {tab === "overview" && (
        <>
          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary/20 bg-primary/5 px-5 py-3.5 mb-5" data-testid="owner-banner">
            <span className="w-2.5 h-2.5 rounded-full bg-primary" />
            <div className="flex-1 text-sm">
              <b>Your bookkeeping is moving along.</b> Your team handled <b>{b.handled_this_week} item{b.handled_this_week === 1 ? "" : "s"}</b> this week.
              {b.needs_you > 0 ? <> <b>{b.needs_you} thing{b.needs_you > 1 ? "s" : ""}</b> from you will help finish {b.period_label}.</> : <> Nothing is waiting on you.</>}
            </div>
            {b.needs_you > 0 && b.needs_you_href && <Button primary onClick={() => navigate(b.needs_you_href)} data-testid="owner-banner-cta">Answer now →</Button>}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BooksCard books={data.books} fmt={fmt} onCatchup={startCatchup}
              onReview={() => navigate(`/accounting/transactions?filter=unapproved&date_from=${data.books.period_start}&date_to=${data.books.period_end}`)} />
            <ProfitCard profit={data.profit} periodLabel={data.period.label.split(" ")[0]} fmt={fmt} />
            <Gate feature="outlook"><CashCard cash={data.cash} fmt={fmt} /></Gate>
            <AttentionCard items={data.attention} onAction={onAttention} />
          </div>
          <TeamStrip team={data.team} companyId={currentId} />
          <div className="flex flex-wrap justify-between text-xs text-slate-500 mt-6">
            <span>{data.books.preliminary ? `Figures are live — preliminary until ${data.books.period_label}'s open items are resolved.` : "Figures are live and reconciled."}</span>
            <span>Last synced {data.books.last_sync_at ? new Date(data.books.last_sync_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</span>
          </div>
        </>
      )}
      {tab === "money" && <MoneyTab data={data} fmt={fmt} companyId={currentId} reload={load} />}
      {tab === "documents" && <DocumentsTab data={data} companyId={currentId} reload={load} />}
      {tab === "team" && <TeamTab data={data} />}
      {tab === "messages" && <MessagesTab companyId={currentId} />}
      {stmtItem && (
        <StatementUploadModal companyId={currentId} item={stmtItem} onClose={() => { setStmtItem(null); load(); }} />
      )}
      {overdueOpen && (
        <OverdueInvoicesModal
          companyId={currentId}
          invoices={data.money.invoices.filter((i) => i.days_overdue > 0)}
          fmt={fmt}
          onClose={() => setOverdueOpen(false)}
          onChanged={load}
        />
      )}
      {receiptsOpen && (
        <MissingReceiptsModal
          companyId={currentId}
          items={(data.attention.find((a) => a.id === "receipts")?.items) || []}
          periodLabel={data.books.period_label}
          onClose={() => setReceiptsOpen(false)}
          onSaved={() => { setReceiptsOpen(false); load(); }}
        />
      )}
    </div>
  );
}
